import React from 'react'
import { useI18nContext } from '../_hooks/I18n'
import {
  StyledAnchor,
  StyledAnchorNavigation,
  StyledAnchorNavigationLandmark,
  StyledAnchorSection,
} from './AnchorNavigation.styles'
import type {
  AnchorNavigationProps,
  AnchorProps,
} from './AnchorNavigation.types'

const Anchor = ({ id, label, selected, onClick }: AnchorProps) => {
  const href = `#${encodeURIComponent(id)}`

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return
    }
    event.preventDefault()
    onClick()
  }

  return (
    <StyledAnchorSection $selected={selected}>
      <StyledAnchor
        href={href}
        aria-current={selected ? 'page' : undefined}
        $selected={selected}
        onClick={handleClick}
      >
        {label}
      </StyledAnchor>
    </StyledAnchorSection>
  )
}

export const AnchorNavigation = React.forwardRef<
  HTMLUListElement,
  AnchorNavigationProps
>(({ sections, selected, onSelect, 'aria-label': ariaLabelProp }, ref) => {
  const i18n = useI18nContext()
  const navigationAriaLabel =
    ariaLabelProp ?? i18n.t('core.anchorNavigation.landmarkLabel')

  return (
    <StyledAnchorNavigationLandmark aria-label={navigationAriaLabel}>
      <StyledAnchorNavigation ref={ref}>
        {sections.map(({ id, label }) => {
          return (
            <Anchor
              key={id}
              id={id}
              onClick={() => onSelect(id)}
              selected={selected === id}
              label={label}
            />
          )
        })}
      </StyledAnchorNavigation>
    </StyledAnchorNavigationLandmark>
  )
})
